import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PaymentSchema,
  SupplierCreditNoteListItemSchema,
  SupplierInvoiceDetailSchema,
  SupplierInvoiceSchema,
} from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, getPage, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ApprovalPanel, workflowHistoryKey } from '../../shared/workflow/ApprovalPanel';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { refreshAfterPosting } from './cache';
import { DocumentLines, Facts, JournalRef, RecordFailure, RecordLink } from './detail';
import { toFils } from './money';
import { RefusalState } from './refusal';

/**
 * One supplier invoice (`/supplier-invoices/:id`): everything recorded about it — supplier,
 * references, dates, lines with their accounts and dimensions, the VAT retained at capture, totals,
 * status and, once posted, its journal (FRD-FIN-070 to 073; SDD §8.9). A direct link or a refresh
 * opens it; posting from here carries `If-Match` like the list.
 *
 * The records that settle it — its credit notes and the payments allocated to it — are listed
 * with links, each only for a session that may read them, and a credit note can be started from
 * here against a posted invoice with a payable outstanding (FRD-FIN-075, FIN-076). The journal
 * links to its own page for a session that may read journals.
 */
export function SupplierInvoiceDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canPost = hasCapability(session.data, 'finance.supplier_invoice:post');
  const canCredit = hasCapability(session.data, 'finance.supplier_credit_note:create');
  const detail = useQuery({
    queryKey: ['supplier-invoice', id],
    queryFn: () => getData(`/api/v1/finance/supplier-invoices/${id}`, SupplierInvoiceDetailSchema),
  });
  const post = useMutation({
    mutationFn: (invoice: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/supplier-invoices/${invoice.id}/post`,
        {},
        SupplierInvoiceSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: invoice.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });
  const canChange = hasCapability(session.data, 'finance.supplier_invoice:change');
  const canCreate = hasCapability(session.data, 'finance.supplier_invoice:create');
  // FRD-FIN-070: verification is recorded; approval follows a configured route.
  const control = useMutation({
    mutationFn: (step: { kind: 'verify' | 'submit'; id: string; version: number }) =>
      postJson(
        `/api/v1/finance/supplier-invoices/${step.id}/${step.kind}`,
        {},
        SupplierInvoiceDetailSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: step.version },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
      await queryClient.invalidateQueries({
        queryKey: workflowHistoryKey('finance.supplier_invoice', id),
      });
    },
  });

  const back = (
    <Button component={RouterLink} to="/supplier-invoices" startIcon={<ArrowBackIcon />}>
      All supplier invoices
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the invoice" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Supplier invoice" noun="invoice" error={detail.error} back={back} />
    );
  }

  const { invoice } = detail.data;
  const creditable =
    canCredit && invoice.status === 'posted' && toFils(invoice.outstandingAmount) > 0n;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Supplier invoice ${invoice.documentNo}`}
        {...(detail.data.supplier.summary === null
          ? {}
          : { description: detail.data.supplier.summary })}
        actions={
          <>
            {back}
            {creditable && (
              <Button
                variant="outlined"
                component={RouterLink}
                to={`/supplier-credit-notes/new?invoice=${invoice.id}`}
              >
                Create credit note
              </Button>
            )}
            {canChange &&
              invoice.status === 'draft' &&
              invoice.verificationStatus === 'unverified' && (
                <Button
                  variant="outlined"
                  disabled={control.isPending}
                  onClick={() => {
                    control.mutate({ kind: 'verify', id: invoice.id, version: invoice.version });
                  }}
                >
                  Mark verified
                </Button>
              )}
            {canCreate &&
              invoice.status === 'draft' &&
              ['not_required', 'returned', 'rejected'].includes(invoice.approvalStatus) && (
                <Button
                  variant="outlined"
                  disabled={control.isPending}
                  onClick={() => {
                    control.mutate({ kind: 'submit', id: invoice.id, version: invoice.version });
                  }}
                >
                  Submit for approval
                </Button>
              )}
            {/* Not while the invoice is in approval: posting follows the approval (FRD-FIN-070). */}
            {canPost && invoice.status === 'draft' && invoice.approvalStatus !== 'pending' && (
              <Button
                variant="contained"
                disabled={post.isPending}
                onClick={() => {
                  post.mutate({ id: invoice.id, version: invoice.version });
                }}
              >
                {post.isPending ? 'Posting…' : 'Post'}
              </Button>
            )}
          </>
        }
      />
      {post.isError && <RefusalState error={post.error} />}
      {control.isError && <RefusalState error={control.error} />}
      <Section title="Invoice">
        <Facts
          label="Invoice details"
          facts={[
            ['Status', <StatusChip key="s" status={invoice.status} />],
            ['Supplier', detail.data.supplier.summary ?? 'Not visible'],
            ["Supplier's invoice number", invoice.supplierInvoiceNumber],
            ['Invoice date', invoice.supplierInvoiceDate],
            ['Tax date', invoice.taxDate],
            ['Due date', invoice.dueDate ?? '—'],
            ['Description', invoice.description ?? '—'],
            ['Net', invoice.netTotal],
            ['VAT', invoice.vatTotal],
            ['Gross', invoice.grossTotal],
            ['Outstanding', invoice.outstandingAmount],
            [
              'Verification',
              invoice.verifiedAt == null
                ? invoice.verificationStatus
                : `${invoice.verificationStatus} on ${invoice.verifiedAt.slice(0, 10)}`,
            ],
            ['Approval', invoice.approvalStatus],
            ['Payment', invoice.paymentStatus.replace('_', ' ')],
            ['Journal', <JournalRef key="journal" journal={detail.data.journal} />],
          ]}
        />
      </Section>
      <Section title="Lines">
        <DocumentLines label="Invoice lines" lines={detail.data.lines} />
      </Section>
      <Settlements invoiceId={invoice.id} />
      <ApprovalPanel
        recordType="finance.supplier_invoice"
        recordId={invoice.id}
        onDecided={() => void queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] })}
      />
      <DocumentsPanel
        recordType="finance.supplier_invoice"
        recordId={invoice.id}
        canAttach={canCreate || canChange}
        title="Supporting documents (the supplier's tax invoice)"
      />
      <RecordHistory recordType="finance.supplier_invoice" recordId={invoice.id} />
    </Stack>
  );
}

/** The credit notes against this invoice and the payments allocated to it, where visible. */
function Settlements({ invoiceId }: { invoiceId: string }) {
  const session = useSession();
  const canSeeCredits = hasCapability(session.data, 'finance.supplier_credit_note:view');
  const canSeePayments = hasCapability(session.data, 'finance.payment:view');
  const credits = useQuery({
    queryKey: ['supplier-credit-notes', 'of-invoice', invoiceId],
    enabled: canSeeCredits,
    queryFn: () =>
      getPage(
        `/api/v1/finance/supplier-credit-notes?pageSize=100&filter[supplier_invoice_id]=${invoiceId}`,
        SupplierCreditNoteListItemSchema,
      ),
  });
  const payments = useQuery({
    queryKey: ['payments', 'of-invoice', invoiceId],
    enabled: canSeePayments,
    queryFn: () =>
      getPage(
        `/api/v1/finance/payments?pageSize=100&filter[supplier_invoice_id]=${invoiceId}`,
        PaymentSchema,
      ),
  });
  return (
    <>
      {canSeeCredits && (
        <Section title="Credit notes">
          {credits.isPending && <LoadingState label="Loading credit notes" />}
          {credits.isError && <ErrorState message={credits.error.message} />}
          {credits.data?.items.length === 0 && (
            <EmptyState title="No credit note against this invoice" />
          )}
          {credits.data !== undefined && credits.data.items.length > 0 && (
            <Table size="small" aria-label="Credit notes against this invoice">
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {credits.data.items.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/supplier-credit-notes/${note.id}`}>
                        {note.documentNo}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{note.supplierCreditNoteReference}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{note.creditNoteDate}</TableCell>
                    <TableCell align="right">{note.grossTotal}</TableCell>
                    <TableCell>
                      <StatusChip status={note.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      {canSeePayments && (
        <Section title="Payments">
          {payments.isPending && <LoadingState label="Loading payments" />}
          {payments.isError && <ErrorState message={payments.error.message} />}
          {payments.data?.items.length === 0 && (
            <EmptyState title="No payment allocated to this invoice" />
          )}
          {payments.data !== undefined && payments.data.items.length > 0 && (
            <Table size="small" aria-label="Payments allocated to this invoice">
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Payment amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.data.items.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/payments/${payment.id}`}>{payment.documentNo}</RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{payment.paymentDate}</TableCell>
                    <TableCell align="right">{payment.amount}</TableCell>
                    <TableCell>
                      <StatusChip status={payment.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </>
  );
}
